import { motion } from "framer-motion";
import { useMemo } from "react";
import { getProducers } from "../../i18n/translations";
import { useLanguage } from "../../i18n/LanguageContext";

function Initials({ name }) {
  const letters = name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2);
  return (
    <div className="absolute inset-0 bg-gradient-to-br from-[#1a1814] to-[#050505] flex items-center justify-center">
      <span className="font-display text-[#C5A059]/80 text-[120px] leading-none select-none">
        {letters}
      </span>
    </div>
  );
}

export default function Producteurs() {
  const { lang, t } = useLanguage();
  const producers = useMemo(() => getProducers(lang), [lang]);

  return (
    <section
      id="producteurs"
      data-testid="producers-section"
      className="relative py-24 md:py-36 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl mb-20 md:mb-28"
        >
          <div className="flex items-center gap-4 mb-6">
            <span className="h-px w-10 bg-[#C5A059]" />
            <span className="tcp-eyebrow">{t.producers.eyebrow}</span>
          </div>
          <h2 className="font-display text-[#F5F5F0] text-4xl sm:text-5xl md:text-6xl leading-[1.05]">
            {t.producers.title}
          </h2>
        </motion.div>

        <div className="space-y-24 md:space-y-32">
          {producers.map((p, i) => (
            <motion.article
              key={p.id}
              data-testid={`producer-${p.id}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, delay: 0.05 }}
              className={`grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center ${
                i % 2 === 1 ? "md:[&>*:first-child]:order-2" : ""
              }`}
            >
              <div className="md:col-span-5">
                <div className="relative aspect-[4/5] w-full overflow-hidden border border-white/10">
                  {p.image ? (
                    <img
                      src={p.image}
                      alt={p.name}
                      className="absolute inset-0 w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <Initials name={p.name} />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                </div>
              </div>

              <div className="md:col-span-7">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-[10px] tracking-[0.32em] uppercase text-[#C5A059]">
                    {p.role}
                  </span>
                </div>
                <h3 className="font-display text-[#F5F5F0] text-4xl md:text-5xl leading-tight">
                  {p.name}
                </h3>

                <div className="mt-7 max-w-2xl space-y-5 text-[#C9C9C2] text-[15px] md:text-base leading-[1.85]">
                  {p.bio.split("\n\n").map((para, idx) => (
                    <p key={idx}>{para}</p>
                  ))}
                </div>

                <a
                  href={`mailto:${p.email}`}
                  data-testid={`producer-email-${p.id}`}
                  className="mt-8 inline-flex items-center gap-3 text-[#F5F5F0] hover:text-[#C5A059] transition-colors group"
                >
                  <span className="font-display text-lg md:text-xl tracking-tight tcp-link">
                    {p.email}
                  </span>
                  <svg width="22" height="8" viewBox="0 0 22 8" fill="none" aria-hidden>
                    <path
                      d="M0 4h20M16 1l4 3-4 3"
                      stroke="currentColor"
                      strokeWidth="1"
                      className="transition-transform duration-500 group-hover:translate-x-1"
                    />
                  </svg>
                </a>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
